import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import env from 'config';
import { Controller } from 'lib_ts/enums/routing/controllers';
import { AuthRoutes } from 'lib_ts/enums/routing/routes/auth';
import {
  ILoginResult,
  IRefreshTokenPayload,
} from 'lib_ts/interfaces/common/i-login';
import { DEFAULT_HEADERS } from 'services/_http-headers';

const REFRESH_TOKEN_PATH = `/${Controller.Auth}/${AuthRoutes.RefreshAuthToken}`;
const REFRESH_TOKEN_URL = `${env.server_url}${REFRESH_TOKEN_PATH}`;

const LOGOUT_PATH = `/${Controller.Auth}/${AuthRoutes.Logout}`;
const LOGIN_AUTH0_PATH = `/${Controller.Auth}/${AuthRoutes.LoginAuth0}`;

interface AxiosRequestWithRetry extends AxiosRequestConfig {
  _isRetry?: boolean; // so we only retry a token-related request failure once
}

export type OnRefreshCallbacks = {
  onRefreshSuccess?: (data: ILoginResult) => void | Promise<void>;
  onRefreshFail?: (error: unknown) => void | Promise<void>;
};

const isPath = (config: AxiosRequestConfig | undefined, path: string) => {
  if (!config?.url) {
    return false;
  }

  // ensure leading slash for comparison - axios config URL doesn't have for some reason
  return `/${config.url}`.includes(path);
};

const isRefreshRequest = (config?: AxiosRequestConfig) =>
  isPath(config, REFRESH_TOKEN_PATH);

const isLogoutRequest = (config?: AxiosRequestConfig) =>
  isPath(config, LOGOUT_PATH);

const isLoginAuth0Request = (config?: AxiosRequestConfig) =>
  isPath(config, LOGIN_AUTH0_PATH);

/** Auth endpoints that rotate or clear cookies. Interceptors must not wait or retry these. */
const isAuthHandshakeRequest = (config?: AxiosRequestConfig) =>
  isRefreshRequest(config) ||
  isLogoutRequest(config) ||
  isLoginAuth0Request(config);

const REFRESH_LOCK_NAME = 'trajekt-refresh-token';
const REFRESH_RETRY_DELAY_MS = 100;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const currentSessionID = (): string => {
  try {
    return sessionStorage.getItem('sessionID') ?? '';
  } catch {
    return '';
  }
};

/**
 * Refresh tokens are single-use: two tabs rotating at once can log the user out.
 * navigator.locks lets only one tab POST at a time. Waiting tabs then use the
 * cookie the first tab already wrote. If the browser has no locks API, skip it.
 */
const withRefreshLock = async <T>(fn: () => Promise<T>): Promise<T> => {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;

  if (!locks?.request) {
    return fn();
  }

  return locks.request(REFRESH_LOCK_NAME, fn);
};

/**
 * Owns token rotation and axios interceptors.
 *
 * Refresh tokens are single-use, so at most one rotate runs at a time.
 * In-tab callers share one in-flight request (fetchPromise).
 *
 * Across tabs, navigator.locks serializes rotates so a second tab waits and
 * then uses the cookie the first tab already set.
 *
 * A 401 is retried once before onRefreshFail.
 *
 * Callbacks are bound from React via setCallbacks (see useBindRefreshHandlers)
 */
export class RefreshTokenService {
  constructor(private http: AxiosInstance) {}

  private onRefreshCallbacks?: OnRefreshCallbacks;

  setCallbacks(callbacks?: OnRefreshCallbacks) {
    this.onRefreshCallbacks = callbacks;
  }

  /** The POST /refresh-auth-token itself. Request interceptor waits on this. */
  private fetchPromise: Promise<ILoginResult> | null = null;

  /** POST + success/fail callbacks. refreshAuthToken callers share this. */
  private refreshPromise: Promise<ILoginResult> | null = null;

  /**
   * Opened during login resume, including Auth0 silent auth before any rotate.
   * 401s wait here instead of starting their own rotate.
   * restoreCount keeps one gate across overlapping begin/end pairs.
   */
  private restoreGate: Promise<void> | null = null;
  private resolveRestoreGate: (() => void) | null = null;
  private restoreCount = 0;

  beginRestore() {
    this.restoreCount += 1;

    if (this.restoreGate) {
      // if a restore gate is already in flight, do not start a new one
      return;
    }

    this.restoreGate = new Promise((resolve) => {
      this.resolveRestoreGate = resolve;
    });
  }

  endRestore() {
    this.restoreCount = Math.max(0, this.restoreCount - 1);

    if (this.restoreCount > 0) {
      // if there are still restore calls in flight, do not resolve the gate
      return;
    }

    this.resolveRestoreGate?.();
    this.restoreGate = null;
    this.resolveRestoreGate = null;
  }

  attachInterceptors() {
    // If a rotate POST is in flight, hold other API calls until cookies are updated.
    this.http.interceptors.request.use(async (config) => {
      if (isAuthHandshakeRequest(config)) {
        return config;
      }

      if (!this.fetchPromise) {
        return config;
      }

      await this.fetchPromise;
      return config;
    });

    this.http.interceptors.response.use(
      (res) => res,
      async (err: AxiosError) => {
        const config = (err.config || {}) as AxiosRequestWithRetry;
        const status = err.response?.status;

        if (isAuthHandshakeRequest(config)) {
          throw err;
        }

        if (status !== 401) {
          throw err;
        }

        if (config._isRetry) {
          throw err;
        }

        if (this.fetchPromise) {
          // Rotate already running — wait, then retry with the new cookies.
          await this.fetchPromise;
        } else if (this.restoreGate && !this.refreshPromise) {
          // Login resume in progress (e.g. Auth0 silent auth) — wait for it.
          await this.restoreGate;
        } else if (!this.restoreGate) {
          await this.refreshAuthToken();
        }

        config._isRetry = true;
        return this.http(config);
      }
    );
  }

  async refreshAuthToken(createNewSessionID?: boolean): Promise<ILoginResult> {
    // Single-flight: a second caller waits for the same rotate.
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Cross-tab: wait our turn so we don't spend the same refresh cookie twice.
    const rotateRequest = withRefreshLock(() =>
      this.performRefresh(createNewSessionID)
    );
    this.fetchPromise = rotateRequest;

    const refresh = (async () => {
      try {
        const result = await rotateRequest;
        // Drop fetchPromise before callbacks so they can use axios
        // (e.g. setCurrentSession) without waiting on this rotate.
        if (this.fetchPromise === rotateRequest) {
          this.fetchPromise = null;
        }
        await this.onRefreshCallbacks?.onRefreshSuccess?.(result);
        return result;
      } catch (error) {
        if (this.fetchPromise === rotateRequest) {
          this.fetchPromise = null;
        }
        await this.onRefreshCallbacks?.onRefreshFail?.(error);
        throw error;
      }
    })();

    this.refreshPromise = refresh;

    try {
      return await refresh;
    } finally {
      if (this.refreshPromise === refresh) {
        this.refreshPromise = null;
      }
    }
  }

  /**
   * Uses fetch, not axios, so this POST does not go through our interceptors.
   * A 401 is retried once: another tab may have already rotated and set new cookies
   * (or we raced before the lock was available).
   */
  private async performRefresh(
    createNewSessionID?: boolean
  ): Promise<ILoginResult> {
    const first = await this.postRefresh(createNewSessionID);
    if (first) {
      return first;
    }

    await delay(REFRESH_RETRY_DELAY_MS);

    const retry = await this.postRefresh(createNewSessionID);
    if (retry) {
      return retry;
    }

    throw new Error('Refresh token failed');
  }

  /** undefined = 401 (retryable). Other failures throw. */
  private async postRefresh(
    createNewSessionID?: boolean
  ): Promise<ILoginResult | undefined> {
    const body = {
      createNewSessionID: !!createNewSessionID,
      // Reuse this tab's session id unless createNewSessionID is set so refreshing doesn't mint a new session id
      currentSessionID: currentSessionID(),
    } as IRefreshTokenPayload;

    const res = await fetch(REFRESH_TOKEN_URL, {
      method: 'POST',
      credentials: 'include',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(body),
    });

    if (res.status === 401) {
      return undefined;
    }

    if (!res.ok) {
      throw new Error(`Refresh token failed (${res.status})`);
    }

    const json = (await res.json()) as ILoginResult;

    if (!json.success) {
      throw new Error('Refresh token failed');
    }

    return json;
  }
}
